import * as React from 'react';
import { config } from '../../platform/index.ts';

export const AUTH_INPUT =
  'h-9 px-3 rounded-[5px] border text-sm outline-none transition-colors disabled:opacity-50 bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active';
export const AUTH_SUBMIT =
  'h-9 rounded-[5px] text-sm font-medium text-white cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed mt-1 bg-nav-active hover:bg-nav-active-dark transition-colors';
export const AUTH_LINK = 'text-sm text-nav-active hover:underline';

/** An error or notice box of the sign-in pages. */
export function AuthNotice({
  kind,
  testId,
  children,
}: {
  kind: 'error' | 'ok';
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`px-4 py-3 rounded-[5px] text-sm ${kind === 'error' ? 'bg-[var(--error-bg)] text-error-text' : 'bg-page-bg text-page-text border border-page-divider'}`}
      data-testid={testId}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {children}
    </div>
  );
}

/** A labelled field of the sign-in pages. */
export function AuthField(
  p: React.InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; testId?: string },
) {
  const { label, testId, ...input } = p;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={p.id} className="text-sm font-medium text-page-text">
        {label}
      </label>
      <input {...input} data-testid={testId} className={AUTH_INPUT} />
    </div>
  );
}

/**
 * The frame of the pages around sign-in (forgot password, reset password, an invitation): the sign-in
 * page's layout, with its own title and card.
 */
export function AuthShell({
  title,
  subtitle,
  testId,
  children,
}: {
  title: string;
  subtitle?: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex" data-testid={testId}>
      <div className="md:hidden fixed top-0 left-0 right-0 h-[72px] flex items-center justify-center z-10 [background:linear-gradient(90deg,var(--nav-from)_0%,var(--nav-to)_100%)]">
        <img src="/logo.svg" alt="Logo" className="h-[40px] w-auto object-contain" />
      </div>
      <div className="flex-1 flex items-center justify-center bg-page-bg px-8 py-12 md:py-12 pt-[104px]">
        <div className="w-full max-w-[380px] flex flex-col gap-8">
          <div className="flex flex-col gap-1.5 text-center md:text-left">
            <h1 className="text-[28px] font-medium leading-tight text-page-title">{title}</h1>
            {subtitle && <p className="text-sm text-page-muted">{subtitle}</p>}
          </div>
          <div className="bg-card-bg rounded-[8px] px-7 py-7 shadow-[0_4px_24px_0_var(--shadow-blue)]">
            {children}
          </div>
        </div>
      </div>
      <div className="hidden md:flex flex-col justify-between w-[45%] px-[60px] py-[50px] nav-sidebar-bg">
        <div className="w-[260px]">
          <img src="/logo.svg" alt="Logo" className="w-full h-auto object-contain" />
        </div>
        <p className="text-xs text-nav-text-faint">TIVA Cortex — Business Automation Platform</p>
      </div>
      <span className="fixed bottom-2 right-3 text-[11px] text-page-muted opacity-50 select-none pointer-events-none">
        v{config.VITE_CORTEX_VERSION}
      </span>
    </div>
  );
}

/** Takes the `#token=…` of a link and removes it from the address bar and history at once. */
export function useFragmentToken(stashKey?: string): string {
  const [token] = React.useState(() => {
    const t = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? '';
    if (t) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      );
      if (stashKey)
        try {
          sessionStorage.setItem(stashKey, t);
        } catch {
          /* a sign-in round trip then needs the link again */
        }
      return t;
    }
    if (stashKey)
      try {
        return sessionStorage.getItem(stashKey) ?? '';
      } catch {
        return '';
      }
    return '';
  });
  return token;
}
